import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  AsyncDataItem,
  AsyncDataItemState,
  IBank,
  idleDataItem,
  IPaginationMeta,
  IPayoutAccount,
  IResolveResult,
  isLoading,
  wrapAsAsyncDataItem
} from '@pharvio/core/shared';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { PayoutAccountsDataService } from '../data/repositories';
import { PAYOUT_ACCOUNTS_DATA_SERVICE } from '../data/tokens';
import {
  ArchivePayoutAccount,
  ClearResolveResult,
  CreatePayoutAccount,
  LoadBanks,
  LoadPayoutAccounts,
  ResolveBankAccount,
  SelectPayoutAccount,
  UpdatePayoutAccountLabel
} from './payout-accounts.actions';

export interface PayoutAccountsStateModel {
  accounts: AsyncDataItem<IPayoutAccount[]>;
  meta: IPaginationMeta | null;
  banks: AsyncDataItem<IBank[]>;
  resolveResult: IResolveResult | null;
  resolving: boolean;
  selectedId: string | null;
  error: string | null;
}

@State<PayoutAccountsStateModel>({
  name: 'payoutAccounts',
  defaults: {
    accounts: idleDataItem([]),
    meta: null,
    banks: idleDataItem([]),
    resolveResult: null,
    resolving: false,
    selectedId: null,
    error: null
  }
})
@Injectable()
export class PayoutAccountsState {
  private readonly svc: PayoutAccountsDataService = inject(
    PAYOUT_ACCOUNTS_DATA_SERVICE
  );

  @Selector()
  static accounts(
    state: PayoutAccountsStateModel
  ): AsyncDataItem<IPayoutAccount[]> {
    return state.accounts;
  }

  @Selector()
  static banks(state: PayoutAccountsStateModel): AsyncDataItem<IBank[]> {
    return state.banks;
  }

  @Selector()
  static loading(state: PayoutAccountsStateModel): boolean {
    return isLoading(state.accounts);
  }

  @Selector()
  static resolveResult(state: PayoutAccountsStateModel): IResolveResult | null {
    return state.resolveResult;
  }

  @Selector()
  static resolving(state: PayoutAccountsStateModel): boolean {
    return state.resolving;
  }

  @Selector()
  static selectedId(state: PayoutAccountsStateModel): string | null {
    return state.selectedId;
  }

  @Selector()
  static error(state: PayoutAccountsStateModel): string | null {
    return state.error;
  }

  @Action(LoadPayoutAccounts)
  loadAccounts(ctx: StateContext<PayoutAccountsStateModel>) {
    const current = ctx.getState().accounts.data;
    const safeData = Array.isArray(current) ? current : [];
    ctx.patchState({
      accounts: wrapAsAsyncDataItem({ data: safeData })
    });
    return this.svc.getAccounts().pipe(
      tap((response) => {
        const accounts = response.data ?? [];
        const meta = response.meta ?? null;
        ctx.patchState({
          accounts: wrapAsAsyncDataItem({
            data: accounts,
            state: AsyncDataItemState.LOADED
          }),
          meta
        });
      }),
      catchError((error) => {
        const curr = ctx.getState().accounts.data;
        ctx.patchState({
          accounts: wrapAsAsyncDataItem({
            data: Array.isArray(curr) ? curr : [],
            state: AsyncDataItemState.ERROR,
            error
          })
        });
        return EMPTY;
      })
    );
  }

  @Action(LoadBanks)
  loadBanks(ctx: StateContext<PayoutAccountsStateModel>) {
    // Session-cache: only load once
    if (ctx.getState().banks.data.length > 0) return EMPTY;
    ctx.patchState({
      banks: wrapAsAsyncDataItem({ data: [] })
    });
    return this.svc.getBanks().pipe(
      tap((banks) =>
        ctx.patchState({
          banks: wrapAsAsyncDataItem({
            data: banks,
            state: AsyncDataItemState.LOADED
          })
        })
      ),
      catchError(() => {
        ctx.patchState({
          banks: wrapAsAsyncDataItem({
            data: [],
            state: AsyncDataItemState.ERROR
          })
        });
        return EMPTY;
      })
    );
  }

  @Action(ResolveBankAccount)
  resolveBankAccount(
    ctx: StateContext<PayoutAccountsStateModel>,
    { bankCode, accountNumber }: ResolveBankAccount
  ) {
    ctx.patchState({ resolving: true, resolveResult: null, error: null });
    return this.svc.resolveBankAccount({ bankCode, accountNumber }).pipe(
      tap((result) =>
        ctx.patchState({ resolveResult: result, resolving: false })
      ),
      catchError((err) => {
        const code = err?.code as string | undefined;
        let error = "We can't reach the bank right now. Try again in a moment.";
        if (code === 'BANK_ACCOUNT_NOT_FOUND') {
          error = `We couldn't find that account at the selected bank. Check the number and try again.`;
        } else if (code === 'BANK_ACCOUNT_INVALID') {
          error = `That number isn't valid for the selected bank. Check you've picked the right bank.`;
        }
        ctx.patchState({ resolving: false, resolveResult: null, error });
        return EMPTY;
      })
    );
  }

  @Action(ClearResolveResult)
  clearResolveResult(ctx: StateContext<PayoutAccountsStateModel>): void {
    ctx.patchState({ resolveResult: null, resolving: false, error: null });
  }

  @Action(CreatePayoutAccount)
  createAccount(
    ctx: StateContext<PayoutAccountsStateModel>,
    { payload }: CreatePayoutAccount
  ) {
    return this.svc.createAccount(payload).pipe(
      tap((created) => {
        const currentData = ctx.getState().accounts.data;
        const existing = Array.isArray(currentData) ? currentData : [];
        // Mark same-scope accounts as ARCHIVED client-side
        const updated = existing.map((a) =>
          a.branchId === payload.branchId && a.status === 'ACTIVE'
            ? { ...a, status: 'ARCHIVED' as const }
            : a
        );
        ctx.patchState({
          accounts: wrapAsAsyncDataItem({
            data: [...updated, created],
            state: AsyncDataItemState.LOADED
          }),
          resolveResult: null
        });
      })
    );
  }

  @Action(ArchivePayoutAccount)
  archiveAccount(
    ctx: StateContext<PayoutAccountsStateModel>,
    { id }: ArchivePayoutAccount
  ) {
    return this.svc.archiveAccount(id).pipe(
      tap(() => {
        const currentData = ctx.getState().accounts.data;
        const existing = Array.isArray(currentData) ? currentData : [];
        const updated = existing.map((a) =>
          a.id === id ? { ...a, status: 'ARCHIVED' as const } : a
        );
        ctx.patchState({
          accounts: wrapAsAsyncDataItem({
            data: updated,
            state: AsyncDataItemState.LOADED
          }),
          selectedId: null
        });
      })
    );
  }

  @Action(UpdatePayoutAccountLabel)
  updateLabel(
    ctx: StateContext<PayoutAccountsStateModel>,
    { id, payload }: UpdatePayoutAccountLabel
  ) {
    return this.svc.updateLabel(id, payload).pipe(
      tap((updated) => {
        const currentData = ctx.getState().accounts.data;
        const existing = Array.isArray(currentData) ? currentData : [];
        const list = existing.map((a) => (a.id === id ? updated : a));
        ctx.patchState({
          accounts: wrapAsAsyncDataItem({
            data: list,
            state: AsyncDataItemState.LOADED
          })
        });
      })
    );
  }

  @Action(SelectPayoutAccount)
  selectAccount(
    ctx: StateContext<PayoutAccountsStateModel>,
    { id }: SelectPayoutAccount
  ): void {
    ctx.patchState({ selectedId: id });
  }
}
